import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_LOW,
  createCommand,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_LEFT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_BACKSPACE_COMMAND,
} from 'lexical';
import { $findMatchingParent, $insertNodeToNearestRoot, mergeRegister } from '@lexical/utils';
import { useEffect } from 'react';
import { $createLayoutContainerNode, $isLayoutContainerNode, LayoutContainerNode } from '../../nodes/LayoutContainerNode';
import { $createLayoutItemNode, $isLayoutItemNode, LayoutItemNode } from '../../nodes/LayoutItemNode';

export const INSERT_LAYOUT_COMMAND = createCommand('INSERT_LAYOUT_COMMAND');

export default function ColumnsLayoutPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([LayoutContainerNode, LayoutItemNode])) {
      throw new Error(
        'ColumnsLayoutPlugin: LayoutContainerNode or LayoutItemNode not registered'
      );
    }

    const $onEscape = (before) => {
      const selection = $getSelection();
      if (
        $isRangeSelection(selection) &&
        selection.isCollapsed() &&
        selection.anchor.offset === 0
      ) {
        const container = $findMatchingParent(
          selection.anchor.getNode(),
          $isLayoutContainerNode,
        );
        if ($isLayoutContainerNode(container)) {
          const parent = container.getParent();
          const child = parent && (before ? parent.getFirstChild() : parent.getLastChild());
          const descendant = before
            ? container.getFirstDescendant()?.getKey()
            : container.getLastDescendant()?.getKey();
          if (parent !== null && child === container && selection.anchor.key === descendant) {
            if (before) {
              container.insertBefore($createParagraphNode());
            } else {
              container.insertAfter($createParagraphNode());
            }
          }
        }
      }
      return false;
    };

    const $fillLayoutItemIfEmpty = (node) => {
      if (node.isEmpty()) {
        node.append($createParagraphNode());
      }
    };

    const $removeIsolatedLayoutItem = (node) => {
      const parent = node.getParent();
      if (!$isLayoutContainerNode(parent)) {
        const children = node.getChildren();
        for (const child of children) {
          node.insertBefore(child);
        }
        node.remove();
        return true;
      }
      return false;
    };

    return mergeRegister(
      // Backspace at the very start of the layout removes the whole container,
      // unwrapping all column content back to normal blocks.
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        () => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection) || !selection.isCollapsed() || selection.anchor.offset !== 0) {
            return false;
          }
          const container = $findMatchingParent(
            selection.anchor.getNode(),
            $isLayoutContainerNode,
          );
          if (!$isLayoutContainerNode(container)) return false;
          // Only trigger when cursor is on the very first descendant of the container
          if (selection.anchor.key !== container.getFirstDescendant()?.getKey()) return false;
          // Unwrap every column's children in order, then remove the container
          const items = container.getChildren();
          for (const item of items) {
            for (const child of item.getChildren()) {
              container.insertBefore(child);
            }
          }
          container.remove();
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),

      // Arrow key escape: insert paragraph above/below when at boundary
      editor.registerCommand(KEY_ARROW_DOWN_COMMAND, () => $onEscape(false), COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ARROW_RIGHT_COMMAND, () => $onEscape(false), COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ARROW_UP_COMMAND, () => $onEscape(true), COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ARROW_LEFT_COMMAND, () => $onEscape(true), COMMAND_PRIORITY_LOW),

      // Insert command
      editor.registerCommand(
        INSERT_LAYOUT_COMMAND,
        (template) => {
          const container = $createLayoutContainerNode(template);
          const columns = template.trim().split(/\s+/).length;
          for (let i = 0; i < columns; i++) {
            container.append($createLayoutItemNode().append($createParagraphNode()));
          }
          $insertNodeToNearestRoot(container);
          container.selectStart();
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),

      // Structure-enforcing transforms: unwrap orphaned items / empty containers
      editor.registerNodeTransform(LayoutItemNode, (node) => {
        const removed = $removeIsolatedLayoutItem(node);
        if (!removed) {
          $fillLayoutItemIfEmpty(node);
        }
      }),

      editor.registerNodeTransform(LayoutContainerNode, (node) => {
        const children = node.getChildren();
        if (!children.every($isLayoutItemNode)) {
          for (const child of children) {
            node.insertBefore(child);
          }
          node.remove();
        }
      }),
    );
  }, [editor]);

  return null;
}
